// Real, framework-free document image processing for the mobile scanner (Phase 1D).
// Operates on plain raster buffers (RGBA) so every function is unit-testable in Node without a
// canvas or a device. Heavy calls are dispatched to a Web Worker by the UI (see scannerProcessor).

export interface RasterImage { data: Uint8ClampedArray; width: number; height: number }
export type Corner = [number, number];
export interface Quad { tl: Corner; tr: Corner; br: Corner; bl: Corner }
export type ColorMode = 'color' | 'grayscale' | 'blackwhite';

function clone(img: RasterImage): RasterImage {
  return { data: new Uint8ClampedArray(img.data), width: img.width, height: img.height };
}

/** Per-pixel luminance (Rec. 601). */
export function luminance(img: RasterImage): Uint8Array {
  const out = new Uint8Array(img.width * img.height);
  for (let i = 0, p = 0; i < img.data.length; i += 4, p += 1) {
    out[p] = Math.round(0.299 * img.data[i] + 0.587 * img.data[i + 1] + 0.114 * img.data[i + 2]);
  }
  return out;
}

export function toGrayscale(img: RasterImage): RasterImage {
  const lum = luminance(img);
  const out = clone(img);
  for (let p = 0; p < lum.length; p += 1) { const v = lum[p]; out.data[p * 4] = out.data[p * 4 + 1] = out.data[p * 4 + 2] = v; }
  return out;
}

/** Otsu global threshold → high-contrast black & white (great for text scans). */
export function toBlackWhite(img: RasterImage): RasterImage {
  const lum = luminance(img);
  const hist = new Array(256).fill(0);
  for (const v of lum) hist[v] += 1;
  const total = lum.length;
  let sum = 0; for (let i = 0; i < 256; i += 1) sum += i * hist[i];
  let sumB = 0, wB = 0, maxVar = -1, threshold = 127;
  for (let t = 0; t < 256; t += 1) {
    wB += hist[t]; if (!wB) continue;
    const wF = total - wB; if (!wF) break;
    sumB += t * hist[t];
    const mB = sumB / wB, mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > maxVar) { maxVar = between; threshold = t; }
  }
  const out = clone(img);
  for (let p = 0; p < lum.length; p += 1) { const v = lum[p] > threshold ? 255 : 0; out.data[p * 4] = out.data[p * 4 + 1] = out.data[p * 4 + 2] = v; }
  return out;
}

/** Linear contrast stretch (maps the observed luminance range to full 0..255). */
export function enhanceContrast(img: RasterImage): RasterImage {
  const lum = luminance(img);
  let min = 255, max = 0;
  for (const v of lum) { if (v < min) min = v; if (v > max) max = v; }
  const range = Math.max(1, max - min);
  const out = clone(img);
  for (let i = 0; i < out.data.length; i += 4) {
    for (let c = 0; c < 3; c += 1) out.data[i + c] = Math.round(((out.data[i + c] - min) / range) * 255);
  }
  return out;
}

/** Reduce uneven lighting/shadows by dividing out a coarse local-mean illumination estimate. */
export function reduceShadows(img: RasterImage, radius = 12): RasterImage {
  const lum = luminance(img);
  const blur = boxBlur(lum, img.width, img.height, radius);
  const out = clone(img);
  for (let p = 0; p < lum.length; p += 1) {
    const factor = blur[p] > 0 ? 128 / blur[p] : 1;
    for (let c = 0; c < 3; c += 1) out.data[p * 4 + c] = clamp255(out.data[p * 4 + c] * factor);
  }
  return out;
}

export function applyColorMode(img: RasterImage, mode: ColorMode): RasterImage {
  if (mode === 'grayscale') return toGrayscale(img);
  if (mode === 'blackwhite') return toBlackWhite(img);
  return clone(img);
}

/**
 * Heuristic document-edge detection: finds the bounding region of high-contrast (document) content
 * against the background using row/column energy of the gradient. This is a robust heuristic, not a
 * full computer-vision contour model (documented in KNOWN_LIMITATIONS).
 */
export function detectDocumentEdges(img: RasterImage): Quad {
  const { width: w, height: h } = img;
  const lum = luminance(img);
  const colEnergy = new Float64Array(w);
  const rowEnergy = new Float64Array(h);
  for (let y = 1; y < h; y += 1) {
    for (let x = 1; x < w; x += 1) {
      const i = y * w + x;
      const g = Math.abs(lum[i] - lum[i - 1]) + Math.abs(lum[i] - lum[i - w]);
      colEnergy[x] += g; rowEnergy[y] += g;
    }
  }
  const left = firstAboveFraction(colEnergy, 0.12);
  const right = lastAboveFraction(colEnergy, 0.12);
  const top = firstAboveFraction(rowEnergy, 0.12);
  const bottom = lastAboveFraction(rowEnergy, 0.12);
  const x0 = Math.min(left, right), x1 = Math.max(left, right);
  const y0 = Math.min(top, bottom), y1 = Math.max(top, bottom);
  if (x1 - x0 < w * 0.1 || y1 - y0 < h * 0.1) {
    const mx = Math.round(w * 0.04), my = Math.round(h * 0.04);
    return { tl: [mx, my], tr: [w - mx, my], br: [w - mx, h - my], bl: [mx, h - my] };
  }
  return { tl: [x0, y0], tr: [x1, y0], br: [x1, y1], bl: [x0, y1] };
}

/** Correct perspective/skew by warping the detected quad to an axis-aligned rectangle. */
export function perspectiveCorrect(img: RasterImage, quad: Quad, outWidth?: number, outHeight?: number): RasterImage {
  const width = Math.max(1, Math.round(outWidth ?? quadWidth(quad)));
  const height = Math.max(1, Math.round(outHeight ?? quadHeight(quad)));
  const dst: Quad = { tl: [0, 0], tr: [width, 0], br: [width, height], bl: [0, height] };
  const H = homography(dst, quad); // map output → source for inverse sampling
  const out: RasterImage = { data: new Uint8ClampedArray(width * height * 4), width, height };
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const denom = H[6] * x + H[7] * y + H[8];
      const sx = (H[0] * x + H[1] * y + H[2]) / denom;
      const sy = (H[3] * x + H[4] * y + H[5]) / denom;
      const o = (y * width + x) * 4;
      sampleBilinear(img, sx, sy, out.data, o);
    }
  }
  return out;
}

/** Mean absolute luminance difference (downsampled) — used to detect a still frame for auto-capture. */
export function frameDifference(a: RasterImage, b: RasterImage): number {
  if (a.width !== b.width || a.height !== b.height) return 1;
  const la = luminance(a), lb = luminance(b);
  const step = Math.max(1, Math.floor(la.length / 4096));
  let sum = 0, count = 0;
  for (let i = 0; i < la.length; i += step) { sum += Math.abs(la[i] - lb[i]); count += 1; }
  return count ? sum / count / 255 : 1;
}

/** Full scan pipeline: detect edges → perspective correct → de-shadow → contrast → color mode. Pure. */
export function runPipeline(img: RasterImage, mode: ColorMode = 'color', quad?: Quad): RasterImage {
  const q = quad ?? detectDocumentEdges(img);
  const warped = perspectiveCorrect(img, q);
  const enhanced = reduceShadows(enhanceContrast(warped));
  return applyColorMode(enhanced, mode);
}

// ── helpers ─────────────────────────────────────────────────────────────────
function clamp255(v: number): number { return v < 0 ? 0 : v > 255 ? 255 : v; }
function quadWidth(q: Quad): number { return Math.max(dist(q.tl, q.tr), dist(q.bl, q.br)); }
function quadHeight(q: Quad): number { return Math.max(dist(q.tl, q.bl), dist(q.tr, q.br)); }
function dist(a: Corner, b: Corner): number { return Math.hypot(a[0] - b[0], a[1] - b[1]); }

function firstAboveFraction(energy: ArrayLike<number>, frac: number): number {
  let max = 0; for (let i = 0; i < energy.length; i += 1) if (energy[i] > max) max = energy[i];
  const t = max * frac;
  for (let i = 0; i < energy.length; i += 1) if (energy[i] >= t) return i;
  return 0;
}
function lastAboveFraction(energy: ArrayLike<number>, frac: number): number {
  let max = 0; for (let i = 0; i < energy.length; i += 1) if (energy[i] > max) max = energy[i];
  const t = max * frac;
  for (let i = energy.length - 1; i >= 0; i -= 1) if (energy[i] >= t) return i;
  return energy.length - 1;
}

function boxBlur(src: Uint8Array, w: number, h: number, r: number): Float64Array {
  const out = new Float64Array(src.length);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      let sum = 0, n = 0;
      for (let dy = -r; dy <= r; dy += r || 1) {
        for (let dx = -r; dx <= r; dx += r || 1) {
          const nx = x + dx, ny = y + dy;
          if (nx >= 0 && nx < w && ny >= 0 && ny < h) { sum += src[ny * w + nx]; n += 1; }
        }
      }
      out[y * w + x] = n ? sum / n : src[y * w + x];
    }
  }
  return out;
}

function sampleBilinear(img: RasterImage, sx: number, sy: number, out: Uint8ClampedArray, o: number): void {
  if (sx < 0 || sy < 0 || sx >= img.width - 1 || sy >= img.height - 1) { out[o] = out[o + 1] = out[o + 2] = 255; out[o + 3] = 255; return; }
  const x0 = Math.floor(sx), y0 = Math.floor(sy);
  const fx = sx - x0, fy = sy - y0;
  const idx = (x: number, y: number) => (y * img.width + x) * 4;
  for (let c = 0; c < 4; c += 1) {
    const a = img.data[idx(x0, y0) + c], b = img.data[idx(x0 + 1, y0) + c];
    const cc = img.data[idx(x0, y0 + 1) + c], d = img.data[idx(x0 + 1, y0 + 1) + c];
    out[o + c] = (a * (1 - fx) + b * fx) * (1 - fy) + (cc * (1 - fx) + d * fx) * fy;
  }
  out[o + 3] = 255;
}

/** Direct-linear-transform homography mapping quad `from` → quad `to` (row-major 3x3). */
export function homography(from: Quad, to: Quad): number[] {
  const s = [from.tl, from.tr, from.br, from.bl];
  const d = [to.tl, to.tr, to.br, to.bl];
  const A: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i += 1) {
    const [sx, sy] = s[i]; const [dx, dy] = d[i];
    A.push([sx, sy, 1, 0, 0, 0, -sx * dx, -sy * dx]); b.push(dx);
    A.push([0, 0, 0, sx, sy, 1, -sx * dy, -sy * dy]); b.push(dy);
  }
  const h = solve8(A, b);
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
}

/** Gaussian elimination for an 8x8 system. */
function solve8(A: number[][], b: number[]): number[] {
  const n = 8;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let r = col + 1; r < n; r += 1) if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r;
    [M[col], M[pivot]] = [M[pivot], M[col]];
    const pv = M[col][col] || 1e-9;
    for (let r = 0; r < n; r += 1) {
      if (r === col) continue;
      const factor = M[r][col] / pv;
      for (let c = col; c <= n; c += 1) M[r][c] -= factor * M[col][c];
    }
  }
  return M.map((row, i) => row[n] / (row[i] || 1e-9));
}
